import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { useEffect, useState, useMemo, useCallback } from 'react';

import { leadBookerPropTypes, partyMemberPropTypes } from './PropTypes';

import { linkPropTypes } from '../../../propTypes';
import Checkbox from '../Atoms/Checkbox';
import Dropdown from '../Atoms/Dropdown';
import Input from '../Atoms/Input';
import Textarea from '../Atoms/Textarea';
import Icon from '../Atoms/Icon';
import { validateAgeForPaxType } from './helpers';
import { MINIMUM_ADULT_AGE } from '../../../config/helpers';

const propTypes = exact({
  adultPassengers: PropTypes.array,
  chestSizeGuideLink: linkPropTypes,
  chestSizeOptions: PropTypes.array,
  chestSizePlaceholderOptionText: PropTypes.string,
  fieldNamePrefix: PropTypes.string.isRequired,
  fieldset: PropTypes.oneOfType([leadBookerPropTypes, partyMemberPropTypes]).isRequired,
  handleBlur: PropTypes.func,
  handleDOBChange: PropTypes.func,
  handleFieldChange: PropTypes.func,
  handleKeyDown: PropTypes.func,
  hideDateOfBirth: PropTypes.bool,
  isBookerAMember: PropTypes.bool,
  isDisabled: PropTypes.bool,
  leadBookerSelectorDefaultOptionText: PropTypes.string,
  leadBookerSelectorLabelText: PropTypes.string,
  onPassengerSelect: PropTypes.func,
  showExtras: PropTypes.bool,
  specialRequirementsLabelText: PropTypes.string,
  specialRequirementsPlaceholderText: PropTypes.string,
  titleOptions: PropTypes.array.isRequired,
  setFieldValueToEmpty: PropTypes.func,
  titlePlaceholderText: PropTypes.string,
  forenamePlaceholderText: PropTypes.string,
  surnamePlaceholderText: PropTypes.string,
  dateOfBirthPlaceholderText: PropTypes.string,
  paxTypes: PropTypes.array,
  paxKey: PropTypes.string,
  validationPaxDateOfBirthInvalid: PropTypes.string,
  validationLeadBookerMinimumBookingAgeNotValid: PropTypes.string,
  selectedPassengerValue: PropTypes.string,
  siteMinimumBookingAge: PropTypes.number,
  tourToDate: PropTypes.string,
});

const defaultProps = {
  adultPassengers: [],
  chestSizeGuideLink: null,
  chestSizeOptions: [],
  chestSizePlaceholderOptionText: '',
  handleBlur: undefined,
  handleDOBChange: () => false,
  handleFieldChange: () => false,
  handleKeyDown: undefined,
  hideDateOfBirth: false,
  isBookerAMember: false,
  isDisabled: false,
  leadBookerSelectorDefaultOptionText: 'Please Select',
  leadBookerSelectorLabelText: 'Please select your name from the passenger list',
  onPassengerSelect: () => false,
  showExtras: false,
  specialRequirementsLabelText: '',
  specialRequirementsPlaceholderText: '',
  setFieldValueToEmpty: () => false,
  titlePlaceholderText: 'Title',
  forenamePlaceholderText: 'First Name',
  surnamePlaceholderText: 'Surname',
  dateOfBirthPlaceholderText: 'Date of Birth (ddmmyyyy)',
  paxTypes: [],
  paxKey: '',
  validationPaxDateOfBirthInvalid: '',
  validationLeadBookerMinimumBookingAgeNotValid: '',
  selectedPassengerValue: '',
  siteMinimumBookingAge: 0,
  tourToDate: '',
};

function MemberDetails({
  adultPassengers,
  chestSizeGuideLink,
  chestSizeOptions,
  chestSizePlaceholderOptionText,
  fieldNamePrefix,
  fieldset,
  handleBlur,
  handleDOBChange,
  handleFieldChange,
  handleKeyDown,
  hideDateOfBirth,
  isBookerAMember,
  isDisabled,
  leadBookerSelectorDefaultOptionText,
  leadBookerSelectorLabelText,
  onPassengerSelect,
  setFieldValueToEmpty,
  showExtras,
  specialRequirementsLabelText,
  specialRequirementsPlaceholderText,
  titleOptions,
  titlePlaceholderText,
  forenamePlaceholderText,
  surnamePlaceholderText,
  dateOfBirthPlaceholderText,
  paxTypes,
  paxKey,
  validationPaxDateOfBirthInvalid,
  validationLeadBookerMinimumBookingAgeNotValid,
  selectedPassengerValue,
  siteMinimumBookingAge,
  tourToDate,
}) {
  const [hasSpecialRequirements, setHasSpecialRequirements] = useState(false);
  const [dobValidation, setDobValidation] = useState({ isInvalid: false, message: '' });

  // Create lead passenger pax type once
  const leadPassengerPaxType = useMemo(() => {
    // Find the Adult pax type to get its maximum age
    const adultPaxType = adultPassengers.find(
      (passenger) => passenger.id === selectedPassengerValue
    );

    const leadPassengerData = {
      key: 'leadPassenger',
      name: 'Lead Passenger',
      minimumAge: isBookerAMember
        ? adultPaxType?.paxAgeRange?.minimumAge || siteMinimumBookingAge || MINIMUM_ADULT_AGE
        : siteMinimumBookingAge,
    };

    if (isBookerAMember) {
      leadPassengerData.maximumAge = adultPaxType?.paxAgeRange?.maximumAge;
    }

    return leadPassengerData;
  }, [siteMinimumBookingAge, paxTypes, isBookerAMember, selectedPassengerValue, adultPassengers]);

  // Memoize validation config to prevent recreating on every render
  const validationConfig = useMemo(
    () => ({
      isLeadParty: fieldNamePrefix === 'leadPartyMember',
      paxTypes: fieldNamePrefix === 'leadPartyMember' ? [leadPassengerPaxType] : paxTypes,
      paxKey: fieldNamePrefix === 'leadPartyMember' ? { value: 'leadPassenger' } : paxKey,
    }),
    [fieldNamePrefix, leadPassengerPaxType, paxTypes, paxKey, isBookerAMember]
  );

  // Handle special requirements
  useEffect(() => {
    if (fieldset.otherRequirements?.value) {
      setHasSpecialRequirements(true);
    }
  }, [fieldset.otherRequirements?.value]);

  // Handle DOB validation
  useEffect(() => {
    if (!fieldset.dateOfBirth?.value) return;

    // Remove slashes from the date before validation
    const dateValue = fieldset.dateOfBirth.value.replace(/\//g, '');

    const validation = validateAgeForPaxType(
      dateValue,
      validationConfig.paxTypes,
      validationConfig.paxKey,
      fieldNamePrefix === 'leadPartyMember'
        ? validationLeadBookerMinimumBookingAgeNotValid
        : validationPaxDateOfBirthInvalid,
      fieldNamePrefix === 'leadPartyMember',
      isBookerAMember,
      siteMinimumBookingAge,
      tourToDate
    );

    setDobValidation({
      isInvalid: !validation.isValid,
      message: validation.message,
    });
  }, [
    fieldset.dateOfBirth?.value,
    validationConfig,
    isBookerAMember,
    validationPaxDateOfBirthInvalid,
    selectedPassengerValue,
    siteMinimumBookingAge,
    tourToDate,
  ]);

  const handleDOBValidation = useCallback(
    (e) => {
      // Remove slashes from the date before validation
      const dateValue = e.target.value.replace(/\//g, '');

      const validation = validateAgeForPaxType(
        dateValue,
        validationConfig.paxTypes,
        validationConfig.paxKey,
        fieldNamePrefix === 'leadPartyMember'
          ? validationLeadBookerMinimumBookingAgeNotValid
          : validationPaxDateOfBirthInvalid,
        fieldNamePrefix === 'leadPartyMember',
        isBookerAMember,
        siteMinimumBookingAge,
        tourToDate
      );

      setDobValidation({
        isInvalid: !validation.isValid,
        message: validation.message,
      });

      handleDOBChange(e);
    },
    [
      validationConfig,
      handleDOBChange,
      validationPaxDateOfBirthInvalid,
      siteMinimumBookingAge,
      tourToDate,
    ]
  );

  const handleOtherRequirementsChange = (value) => {
    setHasSpecialRequirements(value);
    if (setFieldValueToEmpty) setFieldValueToEmpty(`${fieldNamePrefix}.otherRequirements`);
  };

  const mappedTitleOptions = titleOptions.map((titleOption) => ({
    text: titleOption,
    value: titleOption,
  }));

  // format chestSizeOptions array for dropdown
  // key(guid) is value, value(name of chestSize) is text
  let mappedChestSizeOptions = chestSizeOptions.map((chestSizeOption) => ({
    text: chestSizeOption.value,
    value: chestSizeOption.key,
  }));

  if (fieldset.chestSizeKey) {
    if (!mappedChestSizeOptions.length && fieldset.chestSizeKey.value !== '') {
      mappedChestSizeOptions = [
        {
          text: fieldset.chestSizeKey.value,
          value: fieldset.chestSizeKey.value,
        },
      ];
    }
  }

  return (
    <div className="MemberDetails">
      {fieldNamePrefix === 'leadPartyMember' && isBookerAMember && (
        <div className="MemberDetails_passengerLists">
          {leadBookerSelectorLabelText}*
          <Dropdown
            blockName="MemberDetails_field"
            fieldName={`${fieldNamePrefix}.passengerSelect`}
            fieldValue={selectedPassengerValue}
            handleChange={(e) => {
              const selectedPassenger = adultPassengers.find((p) => p.id === e.target.value);
              if (selectedPassenger) {
                onPassengerSelect(selectedPassenger);
              }
            }}
            isDisabled={isDisabled}
            modifier="ghost"
            options={adultPassengers.map((p) => ({
              text: `${p.fullName.forename} ${p.fullName.surname}`,
              value: p.id,
            }))}
            placeholderText={leadBookerSelectorDefaultOptionText}
          />
        </div>
      )}

      <div className="MemberDetails_group">
        <div className="MemberDetails_item MemberDetails_item-1/3">
          <Dropdown
            blockName="MemberDetails_field"
            fieldName={`${fieldNamePrefix}.fullName.prefix`}
            fieldValue={isDisabled ? fieldset.fullName?.prefix : fieldset.fullName?.prefix.value}
            handleChange={handleFieldChange}
            isDisabled={isDisabled}
            isInvalid={fieldset.fullName?.prefix.isInvalid}
            isRequired={fieldset.fullName?.prefix.isRequired}
            modifier="ghost"
            options={mappedTitleOptions}
            placeholderText={titlePlaceholderText}
          />
        </div>
        <div className="MemberDetails_item MemberDetails_item-1/3">
          <Input
            blockName="MemberDetails_field"
            fieldName={`${fieldNamePrefix}.fullName.forename`}
            fieldValue={
              isDisabled ? fieldset.fullName?.forename : fieldset.fullName?.forename.value
            }
            onBlur={handleBlur}
            handleChange={handleFieldChange}
            inputType="text"
            isDisabled={isDisabled}
            isInvalid={fieldset.fullName?.forename.isInvalid}
            isRequired={fieldset.fullName?.forename.isRequired}
            modifier="ghost"
            placeholderText={forenamePlaceholderText}
          />
        </div>
        <div className="MemberDetails_item MemberDetails_item-1/3">
          <Input
            blockName="MemberDetails_field"
            fieldName={`${fieldNamePrefix}.fullName.surname`}
            fieldValue={isDisabled ? fieldset.fullName?.surname : fieldset.fullName?.surname.value}
            onBlur={handleBlur}
            handleChange={handleFieldChange}
            inputType="text"
            isDisabled={isDisabled}
            isInvalid={fieldset.fullName?.surname.isInvalid}
            isRequired={fieldset.fullName?.surname.isRequired}
            modifier="ghost"
            placeholderText={surnamePlaceholderText}
          />
        </div>
      </div>
      <div className="MemberDetails_group">
        <div className="MemberDetails_item MemberDetails_item-1/3">
          {!hideDateOfBirth && (
            <>
              <Input
                blockName="MemberDetails_field"
                fieldName={`${fieldNamePrefix}.dateOfBirth`}
                fieldValue={isDisabled ? fieldset.dateOfBirth : fieldset.dateOfBirth.value}
                onBlur={handleBlur}
                handleChange={handleDOBValidation}
                inputType="text"
                isDisabled={isDisabled}
                isInvalid={fieldset.dateOfBirth.isInvalid}
                isRequired={fieldset.dateOfBirth.isRequired}
                modifier="ghost"
                onKeyDown={handleKeyDown}
                placeholderText={dateOfBirthPlaceholderText}
              />
              {dobValidation.isInvalid && (
                <div className="ErrorMessage_message">
                  <Icon id="tooltip" /> {dobValidation.message}
                </div>
              )}
            </>
          )}
          {showExtras && mappedChestSizeOptions.length > 0 && (
            <>
              <Dropdown
                blockName="MemberDetails_field"
                fieldName={`${fieldNamePrefix}.chestSizeKey`}
                fieldValue={isDisabled ? fieldset.chestSizeKey : fieldset.chestSizeKey.value}
                handleChange={handleFieldChange}
                isDisabled={isDisabled}
                isInvalid={fieldset.chestSizeKey.isInvalid}
                isRequired={fieldset.chestSizeKey.isRequired}
                modifier="ghost"
                options={mappedChestSizeOptions}
                placeholderText={chestSizePlaceholderOptionText}
              />
              {chestSizeGuideLink && (
                <a
                  className="MemberDetails_link"
                  href={chestSizeGuideLink.url}
                  target={chestSizeGuideLink.target}
                  title={chestSizeGuideLink.title}
                >
                  {chestSizeGuideLink.title}
                </a>
              )}
            </>
          )}
        </div>
        {showExtras && (
          <div className="MemberDetails_item MemberDetails_item-2/3">
            <div className="MemberDetails_otherRequirements">
              {!isDisabled && (
                <Checkbox
                  blockName="MemberDetails_otherRequirementsCheckbox"
                  fieldID={`${fieldNamePrefix}.hasSpecialRequirements`}
                  fieldName={`${fieldNamePrefix}.hasSpecialRequirements`}
                  fieldValue="false"
                  handleChange={(e) => handleOtherRequirementsChange(e.target.checked)}
                  inputType="checkbox"
                  isChecked={hasSpecialRequirements}
                  isDisabled={isDisabled}
                  modifier="ghost"
                  text={specialRequirementsLabelText}
                />
              )}
              {(hasSpecialRequirements || (isDisabled && fieldset.otherRequirements)) && (
                <Textarea
                  blockName="MemberDetails_otherRequirementsTextarea"
                  fieldName={`${fieldNamePrefix}.otherRequirements`}
                  fieldValue={
                    isDisabled ? fieldset.otherRequirements : fieldset.otherRequirements.value
                  }
                  handleChange={handleFieldChange}
                  isDisabled={isDisabled}
                  isInvalid={fieldset.otherRequirements.isInvalid}
                  isRequired={fieldset.otherRequirements.isRequired}
                  modifier="ghost"
                  placeholderText={specialRequirementsPlaceholderText}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

MemberDetails.propTypes = propTypes;
MemberDetails.defaultProps = defaultProps;

export default MemberDetails;
